import { useCallback, useEffect, useEffectEvent, useMemo } from 'react'
import isNil from 'lodash/isNil'

import type { ObjectUpdater } from '@app/types/common'

import { ensureType } from '@app/utils/ensureType'
import type { Moment } from '@app/utils/moment'
import moment from '@app/utils/moment'
import { omit } from '@app/utils/omit'
import { pick } from '@app/utils/pick'
import { useAppDispatch } from '@app/utils/redux'

import { getCleanFilter, getDefaultFilter, getEffectiveFilter } from './actions'
import type { AnnouncementsFilter } from './AnnouncementsFilter'

export type AnnouncementsFilterManager = {
  filter: AnnouncementsFilter
  effectiveFilter: AnnouncementsFilter
  activeFiltersCount: number

  set(filter: AnnouncementsFilter): void
  update: ObjectUpdater<AnnouncementsFilter>
  reset(complete?: boolean): void
}

export const useAnnouncementsFilterManager = (
  filter: AnnouncementsFilter,
  onUpdate: (updater: (filter: AnnouncementsFilter) => AnnouncementsFilter) => void,
  now: Moment
): AnnouncementsFilterManager => {
  const dispatch = useAppDispatch()
  const defaultFilter = useMemo(() => dispatch(getDefaultFilter()), [dispatch])
  const effectiveFilter = useMemo(() => {
    return dispatch(getEffectiveFilter(filter))
  }, [dispatch, filter])

  const set = useCallback(
    (filter: AnnouncementsFilter) => {
      onUpdate(() => filter)
    },
    [onUpdate]
  )

  const update = useCallback<ObjectUpdater<AnnouncementsFilter>>(
    (key, value) => {
      onUpdate(filter => {
        if (isNil(value) || value === defaultFilter[key]) {
          return omit(filter, key)
        }

        let newFilter = { ...filter, [key]: value }
        if (key === 'min_kids' && typeof value === 'number' && !isNil(filter.max_kids) && value > filter.max_kids) {
          newFilter = omit(newFilter, 'max_kids')
        }
        if (key === 'max_kids' && typeof value === 'number' && !isNil(filter.min_kids) && value < filter.min_kids) {
          newFilter = omit(newFilter, 'min_kids')
        }
        if (key === 'min_hours' && typeof value === 'number' && filter.max_hours && value > filter.max_hours) {
          newFilter = omit(newFilter, 'max_hours')
        }
        if (key === 'max_hours' && typeof value === 'number' && filter.min_hours && value < filter.min_hours) {
          newFilter = omit(newFilter, 'min_hours')
        }
        if (key === 'min_date') {
          const mid = toMoment(value)
          const mad = toMoment(filter.max_date)
          if (mid && mad && mid.isAfter(mad, 'day')) {
            newFilter = omit(newFilter, 'max_date')
          }
        }
        if (key === 'max_date') {
          const mid = toMoment(filter.min_date)
          const mad = toMoment(value)
          if (mid && mad && mad.isBefore(mid, 'day')) {
            newFilter = omit(newFilter, 'min_date')
          }
        }
        if (key === 'youngest_age_months' && typeof value === 'number' && !isNil(filter.eldest_age_months) && value > filter.eldest_age_months) {
          newFilter = omit(newFilter, 'eldest_age_months')
        }
        if (key === 'eldest_age_months' && typeof value === 'number' && !isNil(filter.youngest_age_months) && value < filter.youngest_age_months) {
          newFilter = omit(newFilter, 'youngest_age_months')
        }

        return newFilter
      })
    },
    [defaultFilter, onUpdate]
  )

  const reset = useCallback(
    (complete = false) => {
      if (complete) {
        onUpdate(() => ({}))
        return
      }
      onUpdate(filter => pick(filter, ...INACTIVE_KEYS))
    },
    [onUpdate]
  )

  const activeFiltersCount = useMemo(() => {
    return ACTIVE_KEYS.reduce((count, key) => {
      const value = filter[key]
      if (isNil(value) || value === defaultFilter[key]) {
        return count
      }
      return count + 1
    }, 0)
  }, [defaultFilter, filter])

  const cleanFilter = useEffectEvent(() => {
    const newFilter = dispatch(getCleanFilter(filter, now))
    if (newFilter !== filter) onUpdate(() => newFilter)
  })

  useEffect(() => {
    cleanFilter()
  }, [now])

  return {
    filter,
    effectiveFilter,
    set,
    update,
    reset,
    activeFiltersCount,
  }
}

const toMoment = (value: unknown) => {
  if (typeof value !== 'string' || !value) return undefined
  return moment(value, 'YYYY-MM-DD')
}

/** keys that are taken into account on active filters count */
const ACTIVE_KEYS_MAP = Object.entries(
  ensureType<{ [K in keyof AnnouncementsFilter]-?: boolean }>({
    order_by: false,
    order_dir: false,
    online: true,
    require_attention: true,
    min_date: true,
    max_date: true,
    min_hours: true,
    max_hours: true,
    start: true,
    end: true,
    min_kids: true,
    max_kids: true,
    youngest_age_months: true,
    eldest_age_months: true,
    regular: true,
    address: true,
    created_by: true,
  })
)

const ACTIVE_KEYS = ACTIVE_KEYS_MAP.filter(([, value]) => value).map(([key]) => key as keyof AnnouncementsFilter)
const INACTIVE_KEYS = ACTIVE_KEYS_MAP.filter(([, value]) => !value).map(([key]) => key as keyof AnnouncementsFilter)
